import { Button, Card, Status, type StatusKind } from "@allergy123/ui";
import { useMemo, useState } from "react";
import {
  calculateProficiency,
  type CalculationStatus,
  type DiameterPair,
  type NegativeControlSite,
} from "./model";

interface PairInput {
  largest: string;
  perpendicular: string;
}

interface NegativeInput {
  wheal: PairInput;
  flare: PairInput;
}

const emptyPair = (): PairInput => ({ largest: "", perpendicular: "" });
const emptyHistamine = () => Array.from({ length: 10 }, emptyPair);
const emptyNegative = () => Array.from({ length: 10 }, () => ({ wheal: emptyPair(), flare: emptyPair() }));

function numberValue(value: string): number | null {
  if (value.trim() === "") return null;
  return Number(value);
}

function pairValue(pair: PairInput): DiameterPair {
  return { largest: numberValue(pair.largest), perpendicular: numberValue(pair.perpendicular) };
}

function format(value: number | null, digits = 2) {
  return value === null ? "—" : value.toFixed(digits);
}

const statusPresentation: Record<CalculationStatus, { kind: StatusKind; label: string }> = {
  incomplete: { kind: "info", label: "Incomplete — enter every required measurement" },
  "invalid-input": { kind: "error", label: "Invalid input — measurements must be zero or greater" },
  "cannot-determine": { kind: "warning", label: "Cannot determine — histamine mean is zero" },
  meets: { kind: "success", label: "Calculation meets the draft control-response standards" },
  "does-not-meet": { kind: "error", label: "Calculation does not meet the draft control-response standards" },
};

const histamineStatusLabel: Record<CalculationStatus, string> = {
  incomplete: "Histamine: enter all 10 wheals",
  "invalid-input": "Histamine: correct measurements below zero or otherwise invalid",
  "cannot-determine": "Histamine: cannot calculate CV when the mean is zero",
  meets: "Histamine: CV is below 30%",
  "does-not-meet": "Histamine: CV is 30% or greater",
};

const negativeStatusLabel: Record<CalculationStatus, string> = {
  incomplete: "Negative controls: enter all 10 wheals and flares",
  "invalid-input": "Negative controls: correct measurements below zero or otherwise invalid",
  "cannot-determine": "Negative controls: cannot determine",
  meets: "Negative controls: every site is below both limits",
  "does-not-meet": "Negative controls: one or more sites are at or above a limit",
};

function MeasurementInput({
  label,
  accessibleLabel,
  value,
  onChange,
}: {
  label: string;
  accessibleLabel: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-small font-medium text-ink">
      <span>{label}</span>
      <span className="relative">
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.1"
          aria-label={accessibleLabel}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-10 w-full rounded-sm border border-rule-strong bg-card px-3 pr-10 text-body text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center font-normal text-ink-secondary">mm</span>
      </span>
    </label>
  );
}

export function SkinTestingProficiencyCalculator() {
  const [histamine, setHistamine] = useState<PairInput[]>(emptyHistamine);
  const [negative, setNegative] = useState<NegativeInput[]>(emptyNegative);
  const result = useMemo(
    () =>
      calculateProficiency(
        histamine.map(pairValue),
        negative.map((site): NegativeControlSite => ({ wheal: pairValue(site.wheal), flare: pairValue(site.flare) })),
      ),
    [histamine, negative],
  );

  const updateHistamine = (index: number, field: keyof PairInput, value: string) => {
    setHistamine((current) => current.map((site, siteIndex) => (siteIndex === index ? { ...site, [field]: value } : site)));
  };

  const updateNegative = (index: number, reaction: keyof NegativeInput, field: keyof PairInput, value: string) => {
    setNegative((current) =>
      current.map((site, siteIndex) =>
        siteIndex === index ? { ...site, [reaction]: { ...site[reaction], [field]: value } } : site,
      ),
    );
  };

  const reset = () => {
    setHistamine(emptyHistamine());
    setNegative(emptyNegative());
  };

  const overall = statusPresentation[result.status];
  const histamineStatus = statusPresentation[result.histamine.status];
  const negativeStatus = statusPresentation[result.negative.status];
  const cvDisplay = result.histamine.coefficientOfVariationPercent === null
    ? "—"
    : `${format(result.histamine.coefficientOfVariationPercent, 1)}%`;

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
      <div className="min-w-0 space-y-10">
        <section aria-labelledby="histamine-heading">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-small font-medium text-ink-secondary">Step 1 of 2</p>
              <h2 id="histamine-heading" className="text-h2 font-semibold text-anchor">Histamine wheals</h2>
              <p className="mt-1 max-w-[68ch] text-ink-secondary">Enter the largest diameter D and its perpendicular diameter d for all ten wheals.</p>
            </div>
            <span className="text-small font-medium text-ink-secondary">{result.histamine.completeSiteCount} of 10 complete</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {histamine.map((site, index) => (
              <Card key={index} as="article" dense className="min-w-0">
                <div className="mb-3 flex items-baseline justify-between gap-3">
                  <h3 className="font-semibold text-anchor">H{index + 1}</h3>
                  <span className="text-small text-ink-secondary">Mean {format(result.histamine.siteMeans[index] ?? null)} mm</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <MeasurementInput label="D · largest" accessibleLabel={`H${index + 1} wheal largest diameter D in millimeters`} value={site.largest} onChange={(value) => updateHistamine(index, "largest", value)} />
                  <MeasurementInput label="d · perpendicular" accessibleLabel={`H${index + 1} wheal perpendicular diameter d in millimeters`} value={site.perpendicular} onChange={(value) => updateHistamine(index, "perpendicular", value)} />
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section aria-labelledby="negative-heading">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-small font-medium text-ink-secondary">Step 2 of 2</p>
              <h2 id="negative-heading" className="text-h2 font-semibold text-anchor">Matched negative controls</h2>
              <p className="mt-1 max-w-[68ch] text-ink-secondary">Enter D and d for every wheal and flare. All ten sites are required.</p>
            </div>
            <span className="text-small font-medium text-ink-secondary">{result.negative.completeSiteCount} of 10 complete</span>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {negative.map((site, index) => (
              <Card key={index} as="article" dense className="min-w-0">
                <div className="mb-3 flex items-baseline justify-between gap-3">
                  <h3 className="font-semibold text-anchor">N{index + 1}</h3>
                  <span className="text-right text-small text-ink-secondary">
                    Mean W {format(result.negative.siteMeans[index]?.wheal ?? null)} · F {format(result.negative.siteMeans[index]?.flare ?? null)} mm
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <MeasurementInput label="Wheal D" accessibleLabel={`N${index + 1} wheal largest diameter D in millimeters`} value={site.wheal.largest} onChange={(value) => updateNegative(index, "wheal", "largest", value)} />
                  <MeasurementInput label="Wheal d" accessibleLabel={`N${index + 1} wheal perpendicular diameter d in millimeters`} value={site.wheal.perpendicular} onChange={(value) => updateNegative(index, "wheal", "perpendicular", value)} />
                  <MeasurementInput label="Flare D" accessibleLabel={`N${index + 1} flare largest diameter D in millimeters`} value={site.flare.largest} onChange={(value) => updateNegative(index, "flare", "largest", value)} />
                  <MeasurementInput label="Flare d" accessibleLabel={`N${index + 1} flare perpendicular diameter d in millimeters`} value={site.flare.perpendicular} onChange={(value) => updateNegative(index, "flare", "perpendicular", value)} />
                </div>
              </Card>
            ))}
          </div>
        </section>
      </div>

      <aside className="space-y-4 xl:sticky xl:top-6" aria-label="Calculation summary">
        <Card tone="tinted">
          <p className="mb-2 text-small font-medium uppercase tracking-wide text-ink-secondary">Calculation result</p>
          <Status kind={overall.kind} label={overall.label} className="items-start text-body" />
          <dl className="mt-6 space-y-4">
            <div>
              <dt className="text-small font-medium text-ink-secondary">Histamine mean</dt>
              <dd className="text-h3 font-semibold text-anchor">{format(result.histamine.mean)} mm</dd>
            </div>
            <div>
              <dt className="text-small font-medium text-ink-secondary">Sample SD</dt>
              <dd className="text-h3 font-semibold text-anchor">{format(result.histamine.sampleStandardDeviation)} mm</dd>
            </div>
            <div>
              <dt className="text-small font-medium text-ink-secondary">Histamine CV</dt>
              <dd className="text-h3 font-semibold text-anchor">{cvDisplay}</dd>
            </div>
          </dl>
        </Card>

        <Card dense>
          <h3 className="mb-3 font-semibold text-anchor">Control checks</h3>
          <div className="space-y-3">
            <Status kind={histamineStatus.kind} label={histamineStatusLabel[result.histamine.status]} className="items-start" />
            <Status kind={negativeStatus.kind} label={negativeStatusLabel[result.negative.status]} className="items-start" />
            {result.negative.failingSites.length > 0 && (
              <p className="text-small text-ink-secondary">Negative sites at or above a limit: {result.negative.failingSites.map((site) => `N${site}`).join(", ")}.</p>
            )}
          </div>
        </Card>

        <Button variant="secondary" onClick={reset} className="w-full">Clear all measurements</Button>
        <p className="text-small text-ink-secondary">Values stay in this browser tab only. Do not enter a subject name or other identifier.</p>
      </aside>
    </div>
  );
}
